import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { ArrowLeftIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';

const VIEW_MODE_KEY = 'poppyruz_mobile_reader_view_mode';

function readStoredViewMode() {
  try {
    const stored = localStorage.getItem(VIEW_MODE_KEY);
    if (stored === 'landscape' || stored === 'portrait') return stored;
  } catch {
    /* ignore */
  }
  return 'portrait';
}

export default function MobileReaderChrome({
  onBack,
  pageIndex,
  totalPages,
  onPrev,
  onNext,
  onJumpToPage,
  canPrev = true,
  canNext = true,
  showFontControls = false,
  onFontDecrease,
  onFontIncrease,
  backgroundThemes = [],
  selectedThemeId,
  onThemeChange,
  backgroundStyle = null,
  children,
}) {
  const [viewMode, setViewMode] = useState(readStoredViewMode);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const stageRef = useRef(null);
  const isLandscape = viewMode === 'landscape';

  const displayPage = pageIndex + 1;
  const pageLabel =
    totalPages > 0 ? `${displayPage} / ${totalPages}` : `${displayPage}`;

  const showThemeSelect = backgroundThemes.length > 0 && onThemeChange;
  const showTopControls = true;
  const canJump = typeof onJumpToPage === 'function' && totalPages > 0;
  const jumpPageOptions = useMemo(() => {
    if (!canJump) return [];
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [canJump, totalPages]);

  const shellBackgroundStyle = isLandscape
    ? { backgroundColor: '#0f172a' }
    : backgroundStyle || { backgroundColor: '#f8fafc' };

  const rotatedBackgroundStyle = backgroundStyle
    ? {
        ...backgroundStyle,
        backgroundAttachment: 'scroll',
      }
    : { backgroundColor: '#f8fafc' };

  const toggleViewMode = useCallback(() => {
    setViewMode((prev) => {
      const next = prev === 'landscape' ? 'portrait' : 'landscape';
      try {
        localStorage.setItem(VIEW_MODE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!isLandscape) return undefined;
    const el = stageRef.current;
    if (!el) return undefined;

    const update = () => {
      const rect = el.getBoundingClientRect();
      setStageSize({ w: rect.width, h: rect.height });
    };
    update();

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [isLandscape]);

  return (
    <div
      className="relative flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden"
      style={shellBackgroundStyle}
    >
      {showTopControls ? (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-50 px-3 pt-3">
          <div className="pointer-events-auto flex items-center gap-2">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                aria-label="Go back"
                className="shrink-0 rounded-full bg-white/90 p-2.5 text-gray-800 shadow-md ring-1 ring-black/5 backdrop-blur-sm active:bg-white"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
            ) : null}

            {showThemeSelect ? (
              <label className="min-w-0 flex-1">
                <span className="sr-only">Background theme</span>
                <select
                  value={selectedThemeId || ''}
                  onChange={(e) => {
                    const theme = backgroundThemes.find(
                      (t) => t.theme_meta_unique_id === e.target.value,
                    );
                    if (theme) onThemeChange(theme);
                  }}
                  className="w-full rounded-full border-0 bg-white/90 py-2.5 pl-3 pr-8 text-sm text-gray-800 shadow-md ring-1 ring-black/5 backdrop-blur-sm"
                >
                  {backgroundThemes.map((theme) => (
                    <option key={theme.theme_meta_unique_id} value={theme.theme_meta_unique_id}>
                      {theme.theme_name}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <div className="min-w-0 flex-1" />
            )}

            {showFontControls ? (
              <div className="flex shrink-0 items-center gap-1 rounded-full bg-white/90 p-1 shadow-md ring-1 ring-black/5 backdrop-blur-sm">
                <button
                  type="button"
                  onClick={onFontDecrease}
                  aria-label="Decrease font size"
                  className="rounded-full px-2.5 py-1.5 text-sm font-medium text-gray-700 active:bg-gray-100"
                >
                  A−
                </button>
                <button
                  type="button"
                  onClick={onFontIncrease}
                  aria-label="Increase font size"
                  className="rounded-full px-2.5 py-1.5 text-sm font-medium text-gray-700 active:bg-gray-100"
                >
                  A+
                </button>
              </div>
            ) : null}

            <button
              type="button"
              onClick={toggleViewMode}
              aria-label={
                isLandscape ? 'Switch to portrait view' : 'Switch to landscape view'
              }
              aria-pressed={isLandscape}
              title={isLandscape ? 'Portrait' : 'Landscape'}
              className="shrink-0 rounded-full bg-white/90 p-2.5 text-gray-800 shadow-md ring-1 ring-black/5 backdrop-blur-sm active:bg-white"
            >
              <DevicePhoneMobileIcon
                className={`h-5 w-5 transition-transform ${isLandscape ? 'rotate-90' : ''}`}
              />
            </button>
          </div>
        </div>
      ) : null}

      <main
        className={`flex min-h-0 flex-1 flex-col ${
          isLandscape ? 'overflow-hidden px-0 pb-0' : 'overflow-y-auto px-3 pb-2'
        } ${showTopControls ? 'pt-20' : 'pt-3'}`}
      >
        {isLandscape ? (
          <div ref={stageRef} className="relative min-h-0 w-full flex-1 overflow-hidden">
            {stageSize.w > 0 && stageSize.h > 0 ? (
              <div
                className="absolute overflow-auto"
                style={{
                  width: stageSize.h,
                  height: stageSize.w,
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%) rotate(90deg)',
                  transformOrigin: 'center center',
                  ...rotatedBackgroundStyle,
                }}
              >
                <div className="box-border min-h-full w-full p-3">{children}</div>
              </div>
            ) : null}
          </div>
        ) : (
          children
        )}
      </main>

      <footer className="sticky bottom-0 z-40 shrink-0 border-t border-gray-200/80 bg-white/95 px-3 py-3 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onPrev}
            disabled={!canPrev}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Prev
          </button>
          {canJump ? (
            <div className="flex items-center gap-1 text-sm font-medium text-gray-600">
              <label className="sr-only" htmlFor="mobile-reader-jump-page">
                Jump to page
              </label>
              <select
                id="mobile-reader-jump-page"
                value={displayPage}
                onChange={(e) => {
                  const next = parseInt(e.target.value, 10);
                  if (!Number.isNaN(next)) onJumpToPage(next);
                }}
                className="max-w-[4.25rem] rounded-md border border-gray-200 bg-white px-1.5 py-1 text-center text-sm font-medium text-gray-800 outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
                aria-label="Jump to page"
              >
                {jumpPageOptions.map((page) => (
                  <option key={page} value={page}>
                    {page}
                  </option>
                ))}
              </select>
              <span>/ {totalPages}</span>
            </div>
          ) : (
            <span className="text-sm font-medium text-gray-600">{pageLabel}</span>
          )}
          <button
            type="button"
            onClick={onNext}
            disabled={!canNext}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </footer>
    </div>
  );
}
